import { useState, useContext, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HiOutlineArrowLongLeft } from "react-icons/hi2";
import { FurnitureSearch } from "../../App.js";
import ProductMockData from "../pages/util/ProductMockData.json";
import { PlusOutlined } from "@ant-design/icons";
import { message } from "antd";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import AlwaysTop from "./util/AlwaysTop.js";
import {
  Grid,
  Typography,
  Box,
  FormControlLabel,
  Checkbox as MuiCheckbox,
} from "@mui/material";
import { putReq } from "../../configs/ApiUrl.js";
import "swiper/css";
import "swiper/css/pagination";
import { useSwiper } from "swiper/react";
import AddonSection from "../admin/AddonSection.js";
import ImageUploadSection from "../admin/ImageUploadSection.js";
import ProductDetailsForm from "../admin/ProductDetailsForm.js";
import SummaryTable from "../admin/SummaryTable.js";
import ApprovalSwiper from "../admin/ApprovalSwiper.js";
import ProductPreviewCard from "../admin/ProductPreviewCard.js";
import AdditionalDetailsForm from "../admin/AdditionalDetailsForm.js";
import PageSection from "../PageSection.js";
import {
  calculatePriceForIteration,
  calculateProductTotal,
  calculateProductAverage,
  calculateDiscountPercentageAverage,
  calculateExtraTotal,
  calculateExtraAverage,
} from "./util/ProductCalculations.js";
import { motion, AnimatePresence } from "framer-motion";
import PageSubheader from "../PageSubheader.js";

// -----------------------------------------------------------------------------
// Global Variables (Do not remove even if not used by the new logic)
// -----------------------------------------------------------------------------
let items = 1;
let priceArray = [1];
let total = 0;
let totalCounter = 0;
let extraCounter = 1;
let extraArray = [];
let extraTotalCounter = [];
let extraTotalSums = [];
let discountDefault = 0;
let priceDefault = 0;
let deliveryDefault = 0;
let productTotalDisplay = 0;
let extraTotalDisplay = [];
let bonusDiscountArray = [];

// -----------------------------------------------------------------------------
// Main Component: AdminProductView
// -----------------------------------------------------------------------------
function AdminProductView() {
  const { t } = useTranslation([
    "admin",
    "forms",
    "tables",
    "addProductPage",
    "productTypes",
    "productPage",
    "altText",
  ]);
  const { options, colors, condition } = ProductMockData;
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  const [searchTerm] = useContext(FurnitureSearch);
  const [declineLoading, setDeclineLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [preApproveLoading, setPreApproveLoading] = useState(false);
  const [existDiscount, setExistDiscount] = useState(null);
  const [isQualityVerified, setIsQualityVerified] = useState(false);
  const [item, setItem] = useState({
    _id: searchTerm?.adminSearch[0]?._id,
    price: searchTerm?.adminSearch[0]?.price,
    model: searchTerm?.adminSearch[0]?.model,
    category: searchTerm?.adminSearch[0]?.category,
    condition: searchTerm?.adminSearch[0]?.condition,
    color: searchTerm?.adminSearch[0]?.color,
    location: searchTerm?.adminSearch[0]?.location,
    title: searchTerm?.adminSearch[0]?.title,
    reason: searchTerm?.adminSearch[0]?.reason,
    rooms: searchTerm?.adminSearch[0]?.rooms || [],
    description: searchTerm?.adminSearch[0]?.description,
    dimension: searchTerm?.adminSearch[0]?.dimension,
    pickUpSlots: searchTerm?.adminSearch[0]?.pickUpSlots,
  });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [mainImage, setMainImage] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [removedImages, setRemovedImages] = useState([]);
  const CROMBO_ORANGE = "#E16A2E";
  const [addedAddons, setAddedAddons] = useState([]);

  const product = useMemo(
    () => searchTerm?.adminSearch[0] || {},
    [searchTerm?.adminSearch[0]?._id]
  );
  const [step, setStep] = useState(0);

  const MAX_STEP = 5;
  const goNext = () => setStep((prev) => Math.min(prev + 1, MAX_STEP));
  const goBack = () => setStep((prev) => Math.max(prev - 1, 0));

  useEffect(() => {
    if (product?.imageNames?.length) {
      const images = product.imageNames.map((imageName, index) => ({
        uid: `${index}`,
        name: imageName,
        status: "done",
        url: imageName,
      }));
      setFileList(images);
      setMainImage(product.mainImage || images[0]?.url);
      setItem((prev) => ({
        ...product,
        ...prev,
        rooms: Array.isArray(product.rooms)
          ? product.rooms
          : typeof product.rooms === "string"
          ? JSON.parse(product.rooms)
          : [],
      }));
    }

    const existingAddons = Array.isArray(product.addOns)
      ? product.addOns
      : typeof product.addOns === "string"
      ? JSON.parse(product.addOns)
      : [];

    setAddedAddons(existingAddons);
    extraArray = [...existingAddons];
    setItem((prev) => ({
      ...prev,
      addOns: [...existingAddons],
    }));

    setExtraDisplay((prev) => ({
      ...prev,
      extras: [...existingAddons],
      counters: existingAddons.map(
        (addon) => addon.quantity || addon.number || 1
      ),
    }));
  }, [product]);

  useEffect(() => {
    AlwaysTop();
  }, []);

  const handleCancel = () => setPreviewOpen(false);
  const handleSwitchChange = (val) => {
    setIsQualityVerified(val);
  };

  const handlePreview = (file) => {
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
    setPreviewTitle(
      file.name || file.url.substring(file.url.lastIndexOf("/") + 1)
    );
  };

  const onDrop = useCallback((acceptedFiles) => {
    const newFileList = acceptedFiles.map((file) => {
      const url = URL.createObjectURL(file);
      return {
        uid: file.name,
        name: file.name,
        status: "done",
        url,
        originFileObj: file,
      };
    });

    setFileList((prevFileList) => [...prevFileList, ...newFileList]);

    return () => {
      newFileList.forEach((file) => URL.revokeObjectURL(file.url));
    };
  }, []);

  const handleRemove = (file) => {
    setFileList((prevFileList) =>
      prevFileList.filter((item) => item.uid !== file.uid)
    );
    setRemovedImages((prevRemovedImages) => [...prevRemovedImages, file.url]);
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept: "image/*",
    maxFiles: 5,
    noClick: true,
    noKeyboard: true,
  });

  const handleMainImageChange = (url) => {
    setMainImage(url);
  };

  const uploadButton = (
    <div
      onClick={open}
      style={{ cursor: "pointer" }}>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  // State variable to change the background to red upon form submit to indicate incomplete fields.
  const [inputDisplay, setInputDisplay] = useState({
    category: searchTerm?.adminSearch[0]?.category || "",
    rooms: searchTerm?.adminSearch[0]?.rooms || [],
    price: searchTerm?.adminSearch[0]?.price || "",
    condition: searchTerm?.adminSearch[0]?.condition || "",
    color: searchTerm?.adminSearch[0]?.color || "",
    height: searchTerm?.adminSearch[0]?.dimension?.height || "",
    width: searchTerm?.adminSearch[0]?.dimension?.width || "",
    depth: searchTerm?.adminSearch[0]?.dimension?.depth || "",
    title: searchTerm?.adminSearch[0]?.title || "",
    reason: searchTerm?.adminSearch[0]?.reason || "",
  });

  // Text warning variable used to request the user to complete fields that were incomplete on submit.
  const [warning, setWarning] = useState("");

  const [itemCounter, setItemCounter] = useState(items);
  const [buttonColor, setButtonColor] = useState({
    minus: "border-cromboOrange text-cromboOrange",
    plus: "border-cromboOrange text-cromboOrange",
  });
  const [priceDisplay, setPriceDisplay] = useState(priceArray);
  const [priceHeader, setPriceHeader] = useState("visible");

  const [extraButtons, setExtraButtons] = useState({
    ...buttonColor,
    minus: "border-cromboOrange text-cromboOrange",
    plus: "border-cromboOrange text-cromboOrange",
  });

  const [extras, setExtras] = useState(extraCounter);

  const [extraDisplay, setExtraDisplay] = useState({
    activeObject: null,
    extras: extraArray,
    counters: extraArray.map(() => 1),
    totalCounters: extraArray.map(() => 0),
    totalSums: extraArray.map(() => 0),
  });

  const [bonusDiscount, setBonusDiscount] = useState(
    extraArray.map(() => false)
  );
  const [itemArrays, setItemArrays] = useState({
    activeObject: null,
    arrays: [],
  });

  const [updateLocation, setUpdateLocation] = useState({
    background: "bg-stone-300",
    text: "text-stone-400",
  });

  const [additionalItems, setAdditionalItems] = useState("text-cromboOrange");

  const [additionalHeaders, setAdditionalHeaders] = useState({
    activeObject: null,
    headers: [],
  });

  const [calculations, setCalculations] = useState({
    productPercentage: discountDefault,
    productPrice: priceDefault,
  });

  const [deliveryCost, setDeliveryCost] = useState(deliveryDefault);
  const [costDelivery, setCostDelivery] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [grandTotalDisplay, setGrandTotalDisplay] = useState("hidden");

  const [calculate, setCalculate] = useState({
    background: "bg-cromboOrange",
    text: "text-white",
  });

  const [discountBonus, setDiscountBonus] = useState(null);
  const [discountVisible, setDiscountVisible] = useState("hidden");
  const [discountNumber, setDiscountNumber] = useState(0);

  // ---------------------------------------------------------------------------
  // Calculation Functions using the Utility Functions
  // ---------------------------------------------------------------------------

  function productTotal() {
    const fullPrice = Number(calculations.productPrice);
    const discount = Number(calculations.productPercentage);
    const total = calculateProductTotal(fullPrice, discount, itemCounter);
    productTotalDisplay = total; // Keep global for consistency
    return total;
  }

  function productPriceAverage() {
    return calculateProductAverage(
      Number(calculations.productPrice),
      Number(calculations.productPercentage),
      itemCounter
    );
  }

  function productDiscountAverage() {
    return itemCounter === 0
      ? 0
      : calculateDiscountPercentageAverage(
          Number(calculations.productPercentage),
          itemCounter
        );
  }

  function discountDisplay() {
    const fullPriceTotal =
      itemCounter * Number(searchTerm?.adminSearch[0]?.price);
    return fullPriceTotal - productTotal();
  }

  function extraItemTotal(i) {
    if (!extraDisplay.extras[i] || extraDisplay.counters[i] === 0) {
      return 0;
    }
    const { price, discount } = extraDisplay.extras[i];
    return calculateExtraTotal(price, discount, extraDisplay.counters[i]);
  }

  function extraDiscountAverage(i) {
    if (!extraDisplay.extras[i] || extraDisplay.counters[i] === 0) {
      return 0;
    }
    return calculateDiscountPercentageAverage(
      extraDisplay.extras[i].discount,
      extraDisplay.counters[i]
    );
  }

  function extraPriceAverage(i) {
    if (!extraDisplay.extras[i] || extraDisplay.counters[i] === 0) {
      return 0;
    }
    const { price, discount } = extraDisplay.extras[i];
    return calculateExtraAverage(price, discount, extraDisplay.counters[i]);
  }

  function delivery() {
    return Number(deliveryCost) || 0;
  }

  function bonusDiscountFunc() {
    return discountBonus ? discountBonus : 0;
  }

  function grandTotal() {
    const extrasTotal = extraDisplay.extras.reduce((sum, extra, i) => {
      return (
        sum +
        calculateExtraTotal(
          extra.price,
          extra.discount,
          extraDisplay.counters[i]
        )
      );
    }, 0);

    return (
      calculateProductTotal(
        Number(calculations.productPrice),
        Number(calculations.productPercentage),
        itemCounter
      ) +
      extrasTotal +
      Number(deliveryCost) -
      (existDiscount || 0) -
      bonusDiscountFunc()
    );
  }

  const SwiperNavButtons = () => {
    const swiper = useSwiper(); // 👈 get instance

    return (
      <>
        <button
          type='button'
          onClick={() => swiper.slidePrev()}
          className='absolute top-1/2 -left-6 z-10 -translate-y-1/2 text-cromboOrange text-4xl font-bold'
          aria-label='Previous'>
          ‹
        </button>
        <button
          type='button'
          onClick={() => swiper.slideNext()}
          className='absolute top-1/2 -right-6 z-10 -translate-y-1/2 text-cromboOrange text-4xl font-bold'
          aria-label='Next'>
          ›
        </button>
      </>
    );
  };

  // Returns the cumulative total for a given count (used in price breakdown list).
  function totalDisplayForCount(count) {
    const priceVal =
      Number(document.getElementById("price")?.value) ||
      Number(item.price) ||
      0;
    const discountVal = Number(document.getElementById("discount")?.value) || 0;
    return calculateProductTotal(priceVal, discountVal, count);
  }

  // ---------------------------------------------------------------------------
  // Other Helper Functions (categoryValue, colorValue, conditionValue, etc.)
  // ---------------------------------------------------------------------------
  function categoryValue() {
    let categoryNumber = 0;
    switch (searchTerm?.adminSearch[0]?.category) {
      case "Appliances":
        categoryNumber = 0;
        break;
      case "Bed":
        categoryNumber = 1;
        break;
      case "Chair":
        categoryNumber = 2;
        break;
      case "Dresser":
        categoryNumber = 3;
        break;
      case "Little Things":
        categoryNumber = 4;
        break;
      case "Sofa":
        categoryNumber = 5;
        break;
      case "Table":
        categoryNumber = 6;
        break;
      case "Wardrobe":
        categoryNumber = 7;
        break;
      default:
        categoryNumber = 0;
    }
    return categoryNumber;
  }

  function colorValue() {
    const color = searchTerm?.adminSearch[0]?.color;
    let colorNumber;
    switch (color) {
      case "Black":
        colorNumber = 0;
        break;
      case "Blue":
        colorNumber = 1;
        break;
      case "Brown":
        colorNumber = 2;
        break;
      case "Gray":
        colorNumber = 3;
        break;
      case "Green":
        colorNumber = 4;
        break;
      case "Orange":
        colorNumber = 5;
        break;
      case "Pink":
        colorNumber = 6;
        break;
      case "Purple":
        colorNumber = 7;
        break;
      case "Red":
        colorNumber = 8;
        break;
      case "White":
        colorNumber = 9;
        break;
      case "Yellow":
        colorNumber = 10;
        break;
      default:
        colorNumber = 0;
    }
    return colorNumber;
  }

  function conditionValue() {
    let conditionNumber = 0;
    switch (searchTerm?.adminSearch[0]?.condition) {
      case "New":
        conditionNumber = 0;
        break;
      case "Good as New":
        conditionNumber = 1;
        break;
      case "Good":
        conditionNumber = 2;
        break;
      case "Fair":
        conditionNumber = 3;
        break;
      case "Poor":
        conditionNumber = 4;
        break;
      default:
        conditionNumber = 0;
    }
    return conditionNumber;
  }

  // ---------------------------------------------------------------------------
  // Event Handlers for Decline, Approve, and Other UI Interactions
  // ---------------------------------------------------------------------------
  const handleDeclineReason = async () => {
    try {
      setDeclineLoading(true);
      const { _id: id } = item;
      if (id) {
        const formData = new FormData();
        formData.append("status", "declined");
        formData.append("reason", item?.reason || "");

        const existingImages = [];
        fileList?.forEach((image) => {
          if (!image?.originFileObj) {
            existingImages.push(image?.name);
          }
        });
        if (existingImages?.length > 0) {
          existingImages.forEach((image, index) => {
            formData.append(`existingImages[${index}]`, image);
          });
        }

        const updateResp = await putReq(`/product/update/${id}`, formData);
        if (updateResp?.success) {
          messageApi.open({
            type: "success",
            content: updateResp?.message,
          });
          setDeclineLoading(false);
          navigate("/admin-product-table");
        } else {
          messageApi.open({
            type: "error",
            content: updateResp?.message,
          });
          setDeclineLoading(false);
        }
      }
    } catch (error) {
      messageApi.open({
        type: "error",
        content: "Something went wrong",
      });
      setDeclineLoading(false);
    }
  };

  const handleApproved = async (status) => {
    try {
      if (status === "approved") {
        setApproveLoading(true);
      } else {
        setPreApproveLoading(true);
      }

      const currentItem = { ...item }; // ✅ Ensure fresh reference to item state
      const formData = new FormData();

      formData.append(
        "status",
        status === "approved" ? "approved" : "preApproved"
      );
      formData.append("title", currentItem?.title);
      formData.append("price", currentItem?.price);
      formData.append("quantity", itemCounter);
      formData.append("discount", existDiscount ?? 0);
      formData.append("bonusDiscount", discountBonus ?? 0);
      formData.append("deliveryPrice", costDelivery ?? 0);
      formData.append("color", currentItem?.color);
      formData.append("category", currentItem?.category);
      formData.append("condition", currentItem?.condition);
      formData.append("isQualityVerified", isQualityVerified);
      formData.append("model", currentItem?.model);
      formData.append("description", currentItem?.description);
      formData.append("reason", currentItem?.reason || "");
      formData.append("dimension", JSON.stringify(currentItem?.dimension));
      formData.append("location", JSON.stringify(currentItem?.location));
      formData.append("removeImages", removedImages.join(","));
      formData.set("rooms", JSON.stringify(currentItem.rooms));

      const mainImageIndex = fileList.findIndex(
        (file) => file.url === mainImage
      );
      formData.append("mainImageIndex", mainImageIndex);
      formData.append("addOns", JSON.stringify(item.addOns || []));

      const existingImages = [];
      fileList?.forEach((image) => {
        if (image?.originFileObj) {
          formData.append("imageNames", image?.originFileObj);
        } else {
          existingImages.push(image?.name);
        }
      });

      if (existingImages.length > 0) {
        existingImages.forEach((image, index) => {
          formData.append(`existingImages[${index}]`, image);
        });
      }

      const productUpdateResp = await putReq(
        `/product/update/${currentItem?._id}`,
        formData
      );

      if (productUpdateResp?.success) {
        setApproveLoading(false);
        setPreApproveLoading(false);
        navigate("/admin-menu");
      } else {
        setApproveLoading(false);
        setPreApproveLoading(false);
      }
    } catch (error) {
      console.error("Error updating product:", error);
      setApproveLoading(false);
      setPreApproveLoading(false);
    }
  };

  const removeSlot = (indexToRemove) => {
    const newTimeSlots = item?.pickUpSlots?.filter(
      (_, index) => index !== indexToRemove
    );
    setItem({
      ...item,
      pickUpSlots: newTimeSlots,
    });
  };

  const handleInputChange = (key, value) => {
    setItem((prevItem) => ({
      ...prevItem,
      [key]: value,
    }));
  };

  // Handler for adding a new add-on from AddonSection
  const handleNewAddon = (newAddon) => {
    setAddedAddons((prev) => [...prev, newAddon]);
    setItem((prev) => ({
      ...prev,
      addOns: [...(prev.addOns || []), newAddon],
    }));
    setExtraDisplay((prev) => ({
      ...prev,
      extras: [...prev.extras, newAddon],
      counters: [...prev.counters, newAddon.quantity],
    }));
    extraArray.push(newAddon);
  };

  const handleRemoveAddon = (indexToRemove) => {
    const removed = addedAddons[indexToRemove];
    setAddedAddons((prev) => prev.filter((_, i) => i !== indexToRemove));
    setItem((prev) => ({
      ...prev,
      addOns: prev.addOns?.filter((_, i) => i !== indexToRemove) || [],
    }));
    setExtraDisplay((prev) => {
      const updatedExtras = prev.extras.filter((_, i) => i !== indexToRemove);
      const updatedCounters = prev.counters.filter(
        (_, i) => i !== indexToRemove
      );
      return {
        ...prev,
        extras: updatedExtras,
        counters: updatedCounters,
      };
    });

    const arrayIndex = extraArray.findIndex((a) => a.item === removed.item);
    if (arrayIndex !== -1) extraArray.splice(arrayIndex, 1);
  };

  useEffect(() => {
    // Update the calculations state immediately when these inputs change.
    setCalculations({
      productPrice: item.price,
      productPercentage: existDiscount || 0,
    });
  }, [item.price, existDiscount, itemCounter, deliveryCost]);

  return (
    <>
      {contextHolder}
      <div className='flex flex-col items-center'>
        <PageSubheader
          backLink='/admin-menu'
          title={t("admin:formTitle")}
        />
        <div
          className='w-full'
          style={{ backgroundColor: "#F4F5F9" }}>
          <main className='px-6 font-bold text-lg'>
            <div className='flex-col justify-center items-center'>
              <Box className='w-full mb-6'>
                <ul className='flex justify-between items-center text-sm font-medium text-gray-500'>
                  {[
                    t("addProductPage:photosHeader"),
                    t("addProductPage:detailsHeader"),
                    t("addProductPage:furtherDetailsHeader"),
                    t("admin:addonHeader"),
                    t("admin:previewHeader"),
                    t("admin:approvalHeader"),
                  ].map((label, index) => (
                    <li
                      key={index}
                      className='flex flex-col items-center w-full'>
                      <div
                        className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border mb-1
          ${
            step === index
              ? "bg-cromboOrange text-white border-cromboOrange"
              : step > index
              ? "bg-cromboOrange text-white border-cromboOrange"
              : "bg-white text-gray-400 border-gray-300"
          }`}
                        onClick={() => setStep(index)}
                        style={{ cursor: "pointer" }}>
                        {index + 1}
                      </div>

                      <div
                        className={`h-1 w-12 rounded-full mt-1
          ${step >= index ? "bg-cromboOrange" : "bg-gray-200"}`}
                      />

                      <div className='mt-2 text-xs text-center hidden md:block'>
                        {label}
                      </div>
                    </li>
                  ))}
                </ul>
              </Box>

              <form>
                <AnimatePresence mode='wait'>
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.3 }}>
                    {step === 0 && (
                      <PageSection translationKey='addProductPage:photosHeader'>
                        <ImageUploadSection
                          getRootProps={getRootProps}
                          getInputProps={getInputProps}
                          fileList={fileList}
                          mainImage={mainImage}
                          previewOpen={previewOpen}
                          previewImage={previewImage}
                          previewTitle={previewTitle}
                          handlePreview={handlePreview}
                          handleRemove={handleRemove}
                          handleMainImageChange={handleMainImageChange}
                          handleCancel={handleCancel}
                          uploadButton={uploadButton}
                          t={t}
                        />
                      </PageSection>
                    )}

                    {step === 1 && (
                      <PageSection translationKey='addProductPage:detailsHeader'>
                        <ProductDetailsForm
                          item={item}
                          setItem={setItem}
                          inputDisplay={inputDisplay}
                          setInputDisplay={setInputDisplay}
                          condition={condition}
                          conditionValue={conditionValue}
                          itemCounter={itemCounter}
                          setItemCounter={setItemCounter}
                          buttonColor={buttonColor}
                          setButtonColor={setButtonColor}
                          priceArray={priceArray}
                          setPriceDisplay={setPriceDisplay}
                          setPriceHeader={setPriceHeader}
                          setCalculate={setCalculate}
                          t={t}
                          deliveryCost={deliveryCost}
                          setDeliveryCost={setDeliveryCost}
                          setCostDelivery={setCostDelivery}
                          setDiscountBonus={setDiscountBonus}
                        />
                      </PageSection>
                    )}

                    {step === 2 && (
                      <PageSection translationKey='addProductPage:furtherDetailsHeader'>
                        <AdditionalDetailsForm
                          item={item}
                          setItem={setItem}
                          inputDisplay={inputDisplay}
                          setInputDisplay={setInputDisplay}
                          options={options}
                          colors={colors}
                          categoryValue={categoryValue}
                          colorValue={colorValue}
                          t={t}
                        />
                      </PageSection>
                    )}

                    {step === 3 && (
                      <PageSection translationKey='addProductPage:includeAddons'>
                        <Box>
                          <AddonSection
                            onAddAddon={handleNewAddon}
                            onRemoveAddon={handleRemoveAddon}
                            addedAddons={addedAddons}
                            t={t}
                          />

                          {showAdvanced && extraDisplay?.extras?.length > 0 && (
                            <Box className='border border-gray-200 rounded-md p-3 mt-2'>
                              <Typography
                                variant='subtitle2'
                                className='font-bold mb-2'>
                                {t("admin:addonSummary") || "Your Add-Ons"}
                              </Typography>
                              {extraDisplay.extras.map((extraItem, i) => (
                                <Box
                                  key={i}
                                  className='mb-2'>
                                  <Typography variant='body2'>
                                    {extraItem.item}
                                  </Typography>
                                  <div className='flex justify-between'>
                                    <span>Total:</span>
                                    <span className='font-bold'>
                                      {extraItemTotal(i)} kr.
                                    </span>
                                  </div>
                                  <div className='mt-1'>
                                    <div className='flex justify-between'>
                                      <span>Unit Discount:</span>
                                      <span className='font-bold'>
                                        {extraDiscountAverage(i)}%
                                      </span>
                                    </div>
                                    <div className='flex justify-between'>
                                      <span>Price per Unit:</span>
                                      <span className='font-bold'>
                                        {extraPriceAverage(i)} kr.
                                      </span>
                                    </div>
                                  </div>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </PageSection>
                    )}

                    {step === 4 && (
                      <ProductPreviewCard
                        item={item}
                        mainImage={mainImage}
                        extraDisplay={extraDisplay}
                        isQualityVerified={isQualityVerified}
                        onQualityChange={setIsQualityVerified}
                        t={t}
                        itemCounter={itemCounter}
                        priceArray={priceArray}
                        showAdvanced={showAdvanced}
                        productTotal={productTotal}
                        productPriceAverage={productPriceAverage}
                        productDiscountAverage={productDiscountAverage}
                        totalDisplayForCount={totalDisplayForCount}
                        extraItemTotal={extraItemTotal}
                        extraPriceAverage={extraPriceAverage}
                        extraDiscountAverage={extraDiscountAverage}
                        delivery={delivery}
                        bonusDiscountFunc={bonusDiscountFunc}
                        grandTotal={grandTotal}
                      />
                    )}
                  </motion.div>
                </AnimatePresence>

                {showAdvanced && (
                  <PageSection translationKey='addProductPage:furtherDetailsHeader'>
                    <SummaryTable
                      item={item}
                      itemCounter={itemCounter}
                      priceArray={priceArray}
                      showAdvanced={showAdvanced}
                      productTotal={productTotal}
                      productPriceAverage={productPriceAverage}
                      productDiscountAverage={productDiscountAverage}
                      totalDisplayForCount={totalDisplayForCount}
                      extraDisplay={extraDisplay}
                      extraItemTotal={extraItemTotal}
                      extraPriceAverage={extraPriceAverage}
                      extraDiscountAverage={extraDiscountAverage}
                      delivery={delivery}
                      bonusDiscountFunc={bonusDiscountFunc}
                      grandTotal={grandTotal}
                      t={t}
                    />
                  </PageSection>
                )}

                {step === 5 && (
                  <PageSection translationKey='admin:approvalHeader'>
                    <ApprovalSwiper
                      item={item}
                      setItem={setItem}
                      inputDisplay={inputDisplay}
                      setInputDisplay={setInputDisplay}
                      handleApproved={handleApproved}
                      handleDeclineReason={handleDeclineReason}
                      approveLoading={approveLoading}
                      preApproveLoading={preApproveLoading}
                      declineLoading={declineLoading}
                      t={t}
                    />
                  </PageSection>
                )}

                <Box className='pb-24 mt-6 flex justify-between'>
                  {step > 0 && (
                    <button
                      type='button'
                      onClick={goBack}
                      className='bg-gray-300 text-black px-4 py-2 rounded shadow capitalize'>
                      {t("forms:back")}
                    </button>
                  )}
                  {step < MAX_STEP && (
                    <button
                      type='button'
                      onClick={goNext}
                      className='bg-cromboOrange text-white px-4 py-2 rounded shadow ml-auto capitalize'>
                      {t("forms:next")}
                    </button>
                  )}
                </Box>

                {step === MAX_STEP + 1 && (
                  <ApprovalSwiper
                    item={item}
                    inputDisplay={inputDisplay}
                    setInputDisplay={setInputDisplay}
                    handleApproved={handleApproved}
                    handleDeclineReason={handleDeclineReason}
                    approveLoading={approveLoading}
                    preApproveLoading={preApproveLoading}
                    declineLoading={declineLoading}
                    t={t}
                  />
                )}
              </form>
            </div>
          </main>
        </div>
      </div>
    </>
  );
}

export default AdminProductView;
